/**
 * canvasAgentState — ce que l'écran rapporte du canvas quand l'avocat envoie un
 * message (lot A de
 * `docs/features/redaction-web-canvas/specs/2026-08-12-canvas-agent-awareness-design.html`).
 *
 * POURQUOI hors de React : la lecture se fait dans
 * `WebSocketPool.sendMessageDirect`, qui n'est pas un composant. Passer par une
 * prop obligerait le composeur à porter un état qui ne le regarde pas, et à le
 * relire au bon instant — un accessoire relu au rendu n'est pas plus frais que
 * l'autorité elle-même (feedback « relire au rendu ≠ seconde garde »). On lit
 * donc le document au moment de l'envoi, qui est le seul instant qui compte.
 *
 * POURQUOI des faits et pas une phrase : la formulation destinée au modèle est
 * une décision de prompt et vit côté backend (`websocket/userStateSuffix.ts`).
 * Ici on ne rapporte que ce que le navigateur constate.
 *
 * Ce module ne décide RIEN — ni l'ouverture du canvas (`state/canvasOpening.ts`,
 * #860), ni le titre ou la nature du document (`state/canvasDocumentMeta.ts`,
 * #1333). Il les LIT. La règle de titre/nature/provenance était auparavant
 * recopiée ici « sur les mêmes sources, sans hook » que celle du panneau : les
 * deux copies avaient déjà divergé sur le titre validé au lancement d'un acte,
 * que le panneau affichait et que le contexte agent taisait.
 */

import type { CanvasAgentState } from '@hector/shared-types';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { lireLeMetaDuScope } from './canvasDocumentMeta';

/**
 * Résout le document du tour AVANT de construire le fait envoyé au modèle.
 *
 * Un scope chaud parqué est toujours le Canvas de sa conversation : une
 * projection d'écran, elle, ne décrirait que le dernier panneau affiché. La
 * consulter ici ferait annoncer A à B quand l'avocat alterne entre les deux
 * onglets.
 *
 * Il n'existe plus de Canvas sans propriétaire à adopter au passage (#1331) :
 * un document naît avec le fil confirmé qui l'a ouvert. Une session
 * appartenant à une autre conversation ne devient donc jamais un repli
 * implicite au moment où l'on construit le message.
 */
function laConversationTientUnCanvas(conversationId: string | null): boolean {
  // ⚠️ Cette fonction RÉSOLVAIT une session documentaire ; il n'y a plus rien à
  // résoudre (#1336), la conversation est le document. Ce qu'elle garde, c'est
  // la seule question qui tranche : ce fil a-t-il un canvas monté ? Rendre la
  // conversation qu'on vient de recevoir aurait été l'alias interdit.
  return conversationId !== null
    && canvasDocumentRuntime.getSessionForConversation(conversationId) !== null;
}

/**
 * État du canvas à joindre au message user, ou `undefined` quand il n'y a rien
 * à dire.
 *
 * Canvas fermé ⟹ `undefined` ⟹ aucun suffixe côté backend : le silence vaut
 * « fermé », convention écrite au modèle dans `prompts/sections/redactionMode.ts`.
 * On envoie l'ÉTAT COMPLET à chaque message, jamais un delta : cet état
 * n'existe nulle part ailleurs dans le contexte du modèle, et un delta
 * laisserait aveugle tout tour où le canvas était déjà ouvert avant le premier
 * message — c'est-à-dire précisément l'incident du 12/08/2026.
 */
export function lireEtatCanvasPourAgent(expediteur: string | null): CanvasAgentState | undefined {
  if (!laConversationTientUnCanvas(expediteur) || expediteur === null) return undefined;
  const meta = lireLeMetaDuScope(expediteur);
  return {
    ouvert: true,
    document: {
      // ⚠️ `provenance: 'vierge'` ne regarde jamais l'éditeur : cela veut dire
      // « ni pièce chargée, ni acte généré ». Un canvas où l'avocat a tapé trois
      // pages le porte encore ; rien en aval ne doit en conclure qu'il est VIDE.
      source: meta.provenance,
      titre: meta.titre,
      // Quel brouillon ce document alimente — c'est ce que `save_document`
      // rangera dans le dossier. Depuis qu'une conversation porte plusieurs
      // documents, « le brouillon du fil » ne désigne plus rien : sans cette
      // annonce, le serveur rangeait le plus récemment MODIFIÉ, qui peut être
      // un document que l'avocat ne regarde pas.
      //
      // ⚠️ C'est le CONTRÔLEUR qui fait foi, pas `meta.restoredDraftId`. Le meta
      // ne connaît que l'identité RESTAURÉE au montage ; le contrôleur porte
      // celle qui part réellement dans le PUT, y compris l'identifiant qu'il a
      // frappé lui-même au premier enregistrement d'une page neuve. Les deux
      // divergent précisément dans le cas qui motive ce champ.
      brouillonId: canvasDocumentRuntime.getDraftController(expediteur)?.getSnapshot().draftId
        ?? null,
    },
  };
}
